import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ListFilter } from 'lucide-react'
import { cn } from '../../utils/cn'

interface HeaderRangeFilterProps {
  label: string
  min: number | null
  max: number | null
  onChange: (range: { min: number | null; max: number | null }) => void
  align?: 'left' | 'right'
}

const DROPDOWN_WIDTH = 240
const DROPDOWN_HEIGHT = 180

// Amount column filter: min/max numeric inputs (a discrete value list is useless
// for currency). Same portal/positioning mechanics as HeaderFilter. Empty inputs
// mean "no bound" — null, never "match nothing".
export function HeaderRangeFilter({ label, min, max, onChange, align = 'right' }: HeaderRangeFilterProps) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0 })
  // Local text state so partially-typed values don't thrash the query.
  const [minText, setMinText] = useState(min == null ? '' : String(min))
  const [maxText, setMaxText] = useState(max == null ? '' : String(max))

  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const active = min != null || max != null

  useEffect(() => { setMinText(min == null ? '' : String(min)) }, [min])
  useEffect(() => { setMaxText(max == null ? '' : String(max)) }, [max])

  const updatePosition = () => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    const openUp = spaceBelow < DROPDOWN_HEIGHT && spaceAbove > spaceBelow
    const rawLeft = align === 'right' ? rect.right - DROPDOWN_WIDTH : rect.left
    const left = Math.max(8, Math.min(rawLeft, window.innerWidth - DROPDOWN_WIDTH - 8))
    setPos({ top: openUp ? rect.top - DROPDOWN_HEIGHT - 4 : rect.bottom + 4, left })
  }

  useLayoutEffect(() => {
    if (open) updatePosition()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    const onMove = () => updatePosition()
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (triggerRef.current?.contains(t)) return
      if (dropdownRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  function parse(text: string): number | null {
    const t = text.trim()
    if (t === '') return null
    const n = Number(t)
    return Number.isFinite(n) ? n : null
  }

  function apply() {
    onChange({ min: parse(minText), max: parse(maxText) })
  }

  function clear() {
    setMinText('')
    setMaxText('')
    onChange({ min: null, max: null })
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        className={cn(
          'inline-flex items-center gap-1 uppercase tracking-wide transition-colors',
          active ? 'text-brand-600' : 'text-gray-400 hover:text-gray-600',
        )}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span>{label}</span>
        <ListFilter className={cn('h-3 w-3', active && 'fill-brand-100')} />
      </button>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: DROPDOWN_WIDTH, zIndex: 60 }}
            className="rounded-xl border border-gray-100 bg-white p-3 shadow-lg"
          >
            <div className="flex items-center gap-2">
              <label className="flex-1">
                <span className="mb-1 block text-[11px] font-medium text-gray-500">Min</span>
                <input
                  type="number"
                  value={minText}
                  onChange={e => setMinText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') apply() }}
                  placeholder="0"
                  className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm tabular-nums focus:border-brand-400 focus:outline-none"
                />
              </label>
              <label className="flex-1">
                <span className="mb-1 block text-[11px] font-medium text-gray-500">Max</span>
                <input
                  type="number"
                  value={maxText}
                  onChange={e => setMaxText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') apply() }}
                  placeholder="∞"
                  className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm tabular-nums focus:border-brand-400 focus:outline-none"
                />
              </label>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <button
                type="button"
                onClick={clear}
                className="text-xs font-medium text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => { apply(); setOpen(false) }}
                className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600"
              >
                Apply
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
